// auth — Google sign-in through Firebase.
//
// The SDK is a DYNAMIC import from gstatic, so an unconfigured app never
// downloads it. That is the difference between "dormant" and "absent": there is
// no cost, no request, and nothing in the network tab.
//
// On native (Capacitor), signInWithPopup does not work in WKWebView. Instead
// we use @capacitor-firebase/authentication for the native Google Sign-In flow,
// then hand the credential to the web Firebase SDK via signInWithCredential.

import { firebaseConfig, googleClientId, isConfigured, signInAvailable } from './firebase-config.js';
import { bus, setState } from '../../src/core/state.js';
import { toast } from '../../src/core/util.js';
import { PUBLIC_ORIGIN } from '../../app-config.js';
import { plugin } from '../../src/core/native.js';

let app = null;
let auth = null;

// The firebase-auth module, kept from ensure().
//
// signInWithPopup must be CALLED inside the click that asked for it. Safari
// grants a page "transient activation" for about a second after a tap, and
// window.open outside that window is a blocked popup. An `await import(...)`
// that has to touch the network spends far more than a second, so re-importing
// the SDK at click time is the difference between a sign-in sheet and silence.
// boot() already warms this, so the click path has no network on it at all.
let sdk = null;

// Resolved the first time Firebase reports an auth state, signed in or not.
//
// Restoring a session is asynchronous — two modules over the network, then
// persistence — and the app asks for the day's board the moment it boots. Ask
// for a token in that window and you get null, the request goes out
// unauthenticated, the server refuses it, and nothing retries: an app that
// says "signed in" on one screen and "sign in" on another. Anything that needs
// a token waits for this first.
let firstAuthState = null;
let settleFirstAuthState = () => {};

const isNative = () => !!globalThis.Capacitor?.isNativePlatform?.();

// Once the browser has nothing better to do, with a ceiling so a busy tab or a
// runtime without `requestIdleCallback` still gets there. Used by boot() only.
function whenIdle(fn) {
  if (typeof window === 'undefined') { fn(); return; }
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 3000 });
  else setTimeout(fn, 300);
}

function origin() {
  return typeof location !== 'undefined' && location.origin !== 'capacitor://localhost' && location.origin !== 'https://localhost'
    ? '' : PUBLIC_ORIGIN;
}

async function checkAllowed(idToken) {
  try {
    const res = await fetch(`${origin()}/api/auth/check`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });
    if (res.status === 403) return false;
    // Only a 403 is a refusal. Anything else that is not ok — a 401 because the
    // server could not verify the token, a 5xx — means the check did not
    // happen, and this lets them in (see onAuthStateChanged) rather than lock
    // them out over it. It should still leave a record that it did.
    if (!res.ok) console.warn('Sign-in: access check answered', res.status);
    return true;
  } catch {
    return true;
  }
}

async function ensure() {
  if (auth) return auth;
  if (!isConfigured()) return null;
  const [{ initializeApp }, authMod] = await Promise.all([
    import('https://www.gstatic.com/firebasejs/10.12.2/firebase-app.js'),
    import('https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js'),
  ]);
  sdk = authMod;
  app = initializeApp(firebaseConfig);
  // Native gets an EXPLICIT persistence chain, and this is the whole bug.
  //
  // getAuth() decides where to keep the session by probing IndexedDB, and
  // Firebase runs every auth operation through one queue behind that probe.
  // Inside a WKWebView served from https://localhost the probe never settles,
  // so signInWithCredential waits forever, onAuthStateChanged never fires, and
  // /api/auth/check is never called — the sign-in attempts collection has not
  // a single row in it. localStorage first, memory if even that is refused;
  // neither one asks IndexedDB anything.
  try {
    auth = isNative()
      ? authMod.initializeAuth(app, {
        persistence: [authMod.browserLocalPersistence, authMod.inMemoryPersistence],
      })
      : authMod.getAuth(app);
  } catch {
    // Already initialised (a hot reload, a second ensure) — take what exists.
    auth = authMod.getAuth(app);
  }
  // Only the WEB build ever redirects, and asking for a redirect result is not
  // free: it builds the redirect resolver, which loads an iframe from the
  // authDomain. Inside a WKWebView served from https://localhost that iframe
  // never answers, and because Firebase runs auth operations through one
  // queue, the signInWithCredential that native sign-in ends with waits behind
  // it forever — the OAuth sheet closes, the plugin resolves, and the button
  // says "Opening Google..." until the app is killed.
  // Coming back from Google IS this call, on the web build. Swallowing its
  // error is why a blocked auth handler looked exactly like nothing happening:
  // the sheet opened, the person signed in, and the app sat there with no user
  // and no message. If it fails now, it says so.
  if (!isNative()) {
    authMod.getRedirectResult(auth).catch((err) => {
      console.error('Sign-in: redirect result failed:', err?.code, err?.message);
      toast(`Sign-in failed: ${err?.code || err?.message || 'redirect'}`);
    });
  }
  let rejecting = false;
  firstAuthState = new Promise((resolve) => { settleFirstAuthState = resolve; });
  authMod.onAuthStateChanged(auth, async (user) => {
    // Before the rejecting guard: a refused sign-in is still an answer, and a
    // token waiter must not be left hanging by one.
    settleFirstAuthState();
    if (rejecting) { rejecting = false; return; }
    if (user) {
      setState({ authChecking: true, authRejected: null });
      // A throw in here used to end the callback where it stood: the user was
      // never written to state, so a successful Google sign-in still showed a
      // signed-out app, and nothing anywhere said why. Failing to VERIFY
      // access is not the same as being refused it, so it lets them in and
      // leaves the record in the console.
      let allowed = true;
      try {
        const token = await user.getIdToken();
        allowed = await checkAllowed(token);
      } catch (err) {
        console.error('Sign-in: could not verify access:', err?.code, err?.message);
      }
      if (!allowed) {
        const email = user.email || '';
        rejecting = true;
        await authMod.signOut(auth);
        setState({ user: null, authChecking: false, authRejected: email });
        bus.emit('auth', null);
        toast(
          email
            ? `${email} doesn't have access to this app. Contact the owner to request access.`
            : `You don't have access to this app. Contact the owner to request access.`,
          { ms: 5000 },
        );
        return;
      }
      setState({ authChecking: false });
    }
    setState({
      user: user ? { uid: user.uid, name: user.displayName, email: user.email } : null,
      authRejected: null,
    });
    bus.emit('auth', user);
  });
  return auth;
}

/**
 * A promise that cannot hang forever.
 *
 * Native sign-in is three hand-offs — plugin, gstatic, Firebase — and any of
 * them can go quiet: the OAuth sheet closes and nothing resolves. An endless
 * "Opening Google…" is worse than an error, because the person cannot tell
 * whether to wait or to try again, and the step name is the only thing that
 * says which hand-off went quiet.
 */
function withTimeout(promise, ms, step) {
  let timer;
  const limit = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`timed out at ${step}`)), ms);
  });
  return Promise.race([promise, limit]).finally(() => clearTimeout(timer));
}

async function signInNative(a) {
  const FirebaseAuthentication = plugin('FirebaseAuthentication');
  if (!FirebaseAuthentication) throw new Error('FirebaseAuthentication plugin not available');
  // Generous: this one is human time, someone picking an account.
  const result = await withTimeout(FirebaseAuthentication.signInWithGoogle(), 90_000, 'google');
  if (!result.credential?.idToken) throw new Error('No credential returned');
  const { GoogleAuthProvider, signInWithCredential } = await withTimeout(import(
    'https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js'), 15_000, 'firebase-sdk');
  const credential = GoogleAuthProvider.credential(result.credential.idToken);
  // Short on purpose: everything after the sheet closes is machine time, so a
  // stall here should name itself while the person is still looking at it.
  await withTimeout(signInWithCredential(a, credential), 20_000, 'firebase-credential');
}

// Popup on every browser, redirect only when the popup is refused.
//
// This used to branch on the user agent: popup on desktop, redirect on mobile.
// Redirect is the one that breaks, and it breaks SILENTLY.
//
// signInWithRedirect finishes by reading the credential back out of the
// authDomain's storage — four-ways-au.firebaseapp.com, a THIRD-PARTY origin to
// legwork.jeremymarks.com.au — through a hidden iframe. Mobile Safari
// partitions storage by top-level site, so the iframe cannot see what the
// handler wrote. getRedirectResult then resolves NULL rather than throwing:
// signing in with Google appeared to work and the app still said "Sign in",
// with nothing in the console and nothing to toast.
//
// Popup carries the credential over postMessage, which is not storage and is
// not partitioned. The cost is that it must run inside the tap (see `sdk`).
async function signInWeb(a) {
  const m = sdk ?? await withTimeout(import(
    'https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js'), 15_000, 'firebase-sdk');
  const provider = new m.GoogleAuthProvider();
  if (googleClientId) provider.setCustomParameters({ client_id: googleClientId });
  try {
    // Human time: someone picking an account, possibly typing a password.
    await withTimeout(m.signInWithPopup(a, provider), 120_000, 'google-popup');
    return;
  } catch (err) {
    const code = err?.code;
    // A refused popup is the only thing worth falling back for. A closed one is
    // a decision, and any other failure is real and must surface.
    if (code !== 'auth/popup-blocked' && code !== 'auth/operation-not-supported-in-this-environment') throw err;
    console.error('Sign-in: popup refused, falling back to redirect:', code);
  }
  await withTimeout(m.signInWithRedirect(a, provider), 20_000, 'redirect');
}

export async function signIn() {
  // ensure() can hang too — it imports two modules over the network and starts
  // persistence — and it runs BEFORE any of the timeouts below, so without
  // this the button could spin on a step that has no name.
  const a = await withTimeout(ensure(), 20_000, 'firebase-init');
  if (!a) return null;
  setState({ authRejected: null });
  try {
    if (isNative() && plugin('FirebaseAuthentication')) {
      await signInNative(a);
    } else {
      await signInWeb(a);
    }
  } catch (err) {
    if (err.code === 'auth/popup-closed-by-user') return null;
    console.error('Sign-in error:', err.code, err.message);
    toast(`Sign-in failed: ${err.code || err.message}`);
  }
  return null;
}

export async function signOut() {
  const a = await ensure();
  if (!a) return;
  const { signOut: fbSignOut } = await import('https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js');
  const FirebaseAuthentication = isNative() ? plugin('FirebaseAuthentication') : null;
  if (FirebaseAuthentication?.signOut) await FirebaseAuthentication.signOut().catch(() => {});
  await fbSignOut(a);
  toast('Signed out');
}

/**
 * A fresh Firebase ID token for the signed-in user, or null.
 *
 * The app's own server verifies this on the routes that cost money to serve
 * (`server/auth.mjs`), so anything calling one needs it. The token is minted by
 * the SDK, refreshed by the SDK, and never stored by us — `getIdToken()` hands
 * back a cached one until it is close to expiry and then quietly renews it.
 */
export async function getIdToken() {
  try {
    await ensure();
    // Wait for the session to be restored, but never forever: a caller that
    // cannot get a token should send none and be refused honestly, rather than
    // hang the screen that asked.
    if (firstAuthState) await withTimeout(firstAuthState, 8_000, 'auth-state').catch(() => {});
    return (await auth?.currentUser?.getIdToken()) ?? null;
  } catch {
    return null;
  }
}

/** The Firebase app instance, for addons that build on auth. Null when off. */
export const firebaseApp = () => app;
export { isConfigured };

export default {
  id: 'auth',

  // WARMED AFTER THE FIRST PAINT, NOT DURING IT.
  //
  // `ensure()` is four third-party requests — firebase-app and firebase-auth
  // from gstatic, then `apis.google.com/js/api.js` and an auth iframe from the
  // authDomain — about 190 KB of somebody else's JavaScript on somebody else's
  // latency. Called straight out of boot, all four were on the critical path of
  // an app shell that had not painted yet, and they were the single largest
  // thing on it: the iframe alone finished 1.4 s in, and the variance of a
  // network nobody here controls is the most likely reason the CI app-shell
  // score has swung from 44 to 78 on identical code.
  //
  // Nothing needs it sooner. `getIdToken()` and `signIn()` both call `ensure()`
  // themselves and get this same promise, so a screen that genuinely wants a
  // token still initialises Firebase on demand and still waits on
  // `firstAuthState` — the warm-up is only here so a TAP on Sign in never has
  // a network import inside Safari's activation window, and an idle callback a
  // second into the session is a thousand times sooner than the first tap.
  async boot() {
    if (!signInAvailable()) return;
    whenIdle(() => { ensure(); });
  },

  settingsRow() {
    if (!signInAvailable()) return null;
    const row = document.createElement('label');
    row.className = 'row';
    row.innerHTML = '<span>Account</span>';
    const btn = document.createElement('button');
    btn.className = 'btn';
    btn.textContent = 'Sign in';

    let signedIn = false;
    bus.on('auth', (user) => {
      signedIn = !!user;
      btn.textContent = user ? 'Sign out' : 'Sign in';
    });
    btn.addEventListener('click', () => (signedIn ? signOut() : signIn()));

    row.appendChild(btn);
    return row;
  },
};
